import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { combineLatestWith, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { AsyncPipe, DatePipe, NgClass, NgStyle } from '@angular/common';
import {
  ApplicationModule, Command2, MenuItem,
  ScannerStore,
  showRequestErrors, SOFTLINE_FEATURE_SCANNER,
  WithActivatedRoute, WithCommands2, WithMenuItems,
  WithRouter,
  WithStaticBackNavigation,
  WithTitle
} from '@softline/application';
import {PageComponent, PageHeaderComponent, TitleBarComponent} from '@softapps/core';
import {
  Base64EncodePipe,
  FormatPipe,
  I18nModule,
  MessageBarStore,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
  UiCoreComponentsModule,
  UiCoreDirectivesModule,
  UiCoreModule
} from '@softline/ui-core';
import { AuftragStore } from '../../store/auftrag.store';
import { Kopfau } from '../../types/kopfau';
import { toSignal } from '@angular/core/rxjs-interop';
import { Artstamm } from '../../types/artstamm';
import { BelegvermerkStore } from '../../store/belegvermerk.store';
import { BeweauStore } from '../../store/beweau.store';
import { BeweauCardComponent } from '../../components/beweau-card/beweau-card.component';
import { TimeReportStore } from '../../store/timeReport.store';
import moment from 'moment/moment';
import { DateService, Store } from '@softline/core';
import {
  ChangePickUpDateDialogComponent
} from '../../dialogs/change-pick-up-date-dialog/change-pick-up-date-dialog.component';
import { ImageViewerDialogComponent } from '../../dialogs/image-viewer-dialog/image-viewer-dialog.component';
import { ChangePriceComponent } from '../../dialogs/change-price/change-price.component';
import { MaterialListComponent } from '../../dialogs/material-list/material-list.component';
import { CreateBelegvermerkComponent } from '../../dialogs/create-belegvermerk/create-belegvermerk.component';
import { Belegvermerk } from '../../types/belegvermerk';
import { AuthenticationContextStore, SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT } from '@softline/auth';

@Component({
    selector: 'soft-kopfau',
    templateUrl: './kopfau.component.html',
    styleUrls: ['./kopfau.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PageComponent,
        PageHeaderComponent,
        I18nModule,
        ApplicationModule,
        UiCoreComponentsModule,
        BeweauCardComponent,
        DatePipe,
        AsyncPipe,
        UiCoreDirectivesModule,
        NgStyle,
        NgClass,
        Base64EncodePipe,
        UiCoreModule
    ]
})
export class KopfauComponent extends (
  WithTitle(
  WithStaticBackNavigation('/goldschmiede',
  WithActivatedRoute(
  WithRouter(
  ))))) implements OnInit, OnDestroy {

  private readonly injector = inject(Injector);

  private readonly auftragStore = inject(AuftragStore);
  private readonly belegvermerkStore = inject(BelegvermerkStore);
  private readonly beweauStore = inject(BeweauStore);
  private readonly legacyStore = inject(Store);
  private readonly timeReportStore = inject(TimeReportStore);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly dateService = inject(DateService);

  readonly goldschmiedeText = signal<string | null>(null);

  readonly id$ = this.route.paramMap.pipe(
    map(o => o.get('id')),
    filter(o => !!o && o?.length > 0),
    distinctUntilChanged()
  );

  readonly entity = toSignal<Kopfau | null>(
    this.auftragStore.collection.state$.pipe(
      combineLatestWith(this.id$),
      map(([state, id]) => {
        if (!id)
          return null;

        return state.dict[id] ?? null;
      })
    )
  );

  readonly title = signal(this.entity()?.hauptartikel.artbez1 ?? '');
  readonly id = toSignal(this.id$);
  readonly image = signal<any[]>([]);
  readonly isChangingWarranty = this.auftragStore.collectionRemote.savingState;
  readonly belegvermerke = this.belegvermerkStore.collection.all;
  readonly beweaus = this.beweauStore.collection.all;
  readonly timeReports = this.timeReportStore.collection.all;

  readonly timeSum = computed(() => {
    const timeReports = this.timeReports();
    const totalDuration = timeReports.reduce((p, c) => p.add(c.duration), moment.duration(0));
    if (totalDuration.asMilliseconds() === 0) {
      return '00:00';
    }
    return moment.utc(totalDuration.asMilliseconds()).format("HH:mm");
  });

  constructor(private store: Store) {
    super();
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();

    const id = this.id();
    const entity = this.entity();

    if (!entity || !id) return;

    const artikelId = entity?.hauptartikel?.id;
    if (!artikelId) return;

    const imageBlob = await this.auftragStore.auftragStoreAddon.loadPicture(artikelId+'');
    this.image.set([imageBlob]);

    await this.belegvermerkStore.collectionRemote.loadMany({
      pathParams: { idkopfau: entity.id }
    })

    await this.beweauStore.collectionRemote.loadMany({
      pathParams: { idkopfau: entity.id }
    })

    const formatPipe = inject(FormatPipe);
    const startDate = formatPipe.transform(moment(this.dateService.today()).subtract(1, 'year').format('YYYY-MM-DD'), 'YYYY-MM-DD');
    const endDate = formatPipe.transform(moment(new Date()).endOf('year').format('YYYY-MM-DD'), 'YYYY-MM-DD');

    const user = this.legacyStore.get(
      SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
      AuthenticationContextStore.getters.data
    ) as { userId: number };

    this.timeReportStore.collection.clear();

    await this.timeReportStore.collectionRemote.loadMany({
      query: {
        userId: user.userId,
        objectId: entity.objekt.id,
        to: endDate,
        from: startDate,
      }
    })
  }

  override async ngOnDestroy() {
    this.timeReportStore.collection.resetState();
    super.ngOnDestroy();
  }

  async showCreateBelegvermerk(auftrag: Kopfau): Promise<void> {
    const result = await this.legacyStore.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open(), {
        id: 'GOLDSCHMIEDE_CREATE_BELEGVERMERK',
        component: CreateBelegvermerkComponent,
    });

    if (!result || result == 'DISMISSED') return;

    await this.createBelegvermerk(result.toString(), auftrag?.id);
  }

  private async createBelegvermerk(text: string | null, idkopfau: number) : Promise<void> {
    if (text === null || text?.length < 1) {
      return;
    }

    const anwender: any = this.legacyStore.get(
      SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
      AuthenticationContextStore.getters.data
      );

    const entity: Belegvermerk = {
      id: null,
      anwender: anwender?.userId,
      erfzeit: new Date().toISOString(),
      vondatum: new Date().toISOString().substring(0, 10),
      vermerk: text
    }
  }

  async changePickUpDate(entity: Kopfau) {
    const result = await this.legacyStore.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open(), {
      id: 'GOLDSCHMIEDE_CHANGE_PICKUP_DATE',
      component: ChangePickUpDateDialogComponent,
      data: {
        currentDate: entity?.abholdatum
      }
    });

    if (!result || result === 'DISMISSED') return;

    await this.updateKopfau(
      { ...entity, ...result },
      'Abholdatum wurde geändert',
    )
  }

  async changeWarranty(entity: Kopfau): Promise<void> {
    const updatedEntity: Kopfau = {
      ...entity,
      kzgarantie: entity?.kzgarantie === 'N' ? 'J' : 'N'
    }

    await this.updateKopfau(
      updatedEntity,
      '#GOLDSCHMIEDE.KOPFAU.MESSAGES.UPDATE_GARANTIE_SUCCESS'
    );
  }

  async saveGoldschmiedeText(entity: Kopfau): Promise<void> {
    const updatedEntity: Kopfau = {
      ...entity,
      goldschmiedeText: this.goldschmiedeText()
    }

    await this.updateKopfau(
      updatedEntity,
      '#GOLDSCHMIEDE.KOPFAU.MESSAGES.UPDATE_TEXT_SUCCESS'
    );
  }

  updateGoldschmiedeText(text: string): void {
    this.goldschmiedeText.set(text);
  }

  async updateKopfau(entity: Kopfau, msg: string): Promise<void> {
    try {
      await this.auftragStore.collectionRemote.update({
        value: entity,
        pathParams: { id: entity.id },
      });

      await this.legacyStore.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        msg
      );
    } catch (e) {
      showRequestErrors(this.legacyStore, e);
    }
  }

  async showZoomDialog(hauptartikel: Artstamm) {
    await this.legacyStore.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), {
      id: 'GOLDSCHMIEDE_IMAGE_VIEWER',
      component: ImageViewerDialogComponent,
      dismiss: {
        escape: true,
        button: true,
        backdrop: true
      },
      getInjector: (): Injector => this.injector,
      data: {
        artstamm: hauptartikel
      },
    })
  }

  async showChangePriceDialog(entity: Kopfau) {
    const newPrice = await this.legacyStore.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open(), {
      id: 'GOLDSCHMIEDE_CHANGE_PRICE',
      component: ChangePriceComponent,
      data: {
        price: entity.vkpreis,
        preisebene: entity?.preisebene
      },
      dismiss: { escape: true, button: true, backdrop: true }
    })
    if (!newPrice || newPrice === 'DISMISSED') //newPrice <= 0
      return;

    await this.updateKopfau(
      { ...entity, ...newPrice },
      ''
    )
  }

  async showBeweaus(): Promise<void> {
    await this.legacyStore.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), {
      id: 'GOLDSCHMIEDE_MATERIAL_LIST',
      component: MaterialListComponent,
      data: { beweaus: this.beweaus() },
      dismiss: { button: true, escape: true, backdrop: true}
    })
  }
}
